import {
  EmptyPageComponent,
  LoaderComponent,
  PaginationComponent,
  SearchbarComponent,
  SynDialogConfig,
} from '$synapse/core';
import {
  Assessment,
  Candidate,
  ExamStatus,
  Group,
  Page,
} from '$synapse/models';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { first, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CandidateCreationModalComponent } from './candidate-creation-modal/candidate-creation-modal.component';
import {
  Filter as PreciseFilter,
  FilterComponent,
} from './filter/filter.component';
import { MatDialog } from '@angular/material/dialog';
import { CandidateActionsComponent } from './candidate-actions/candidate-actions.component';
import { CandidatesTableComponent } from '../../core/components/candidates-table/candidates-table.component';
import { SHARED_IMPORTS } from '../../core/shared-imports';

type Filter = {
  search: string;
  assessments: Assessment[];
  groups: Group[];
  status: ExamStatus[];
};

@Component({
  selector: 'syn-candidates',
  templateUrl: './candidates.component.html',
  styleUrls: ['./candidates.component.scss'],
  imports: [
    SHARED_IMPORTS,
    LoaderComponent,
    CandidateActionsComponent,
    SearchbarComponent,
    FilterComponent,
    CandidatesTableComponent,
    EmptyPageComponent,
    PaginationComponent,
  ],
})
export class CandidatesComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _modalDialog = inject(MatDialog);

  readonly isLoading = false;
  readonly page$: Observable<Page<Candidate>> = this._route.data.pipe(
    map((data) => data.candidates)
  );

  numberOfCandidates: number;

  filter: Filter = {
    search: '',
    assessments: [],
    groups: [],
    status: [],
  };

  ngOnInit(): void {
    this.page$.subscribe(
      (candidates) =>
        (this.numberOfCandidates = candidates.totalPages * candidates.limit)
    );
  }

  onSearchFilterChange(newSearch: string) {
    this.filter.search = newSearch;
  }

  resetSearch() {
    this.filter.search = '';
    this.applyFilter();
  }

  onFilterAssessmentChange(newFilter: PreciseFilter) {
    this.filter = {
      assessments: newFilter.assessments,
      search: this.filter.search,
      groups: this.filter.groups,
      status: this.filter.status,
    } as Filter;
    this.applyFilter();
  }

  onFilterGroupChange(newFilter: PreciseFilter) {
    this.filter = {
      groups: newFilter.groups,
      search: this.filter.search,
      assessments: this.filter.assessments,
      status: this.filter.status,
    } as Filter;
    this.applyFilter();
  }

  onFilterStatusChange(newFilter: PreciseFilter) {
    this.filter = {
      status: newFilter.status,
      search: this.filter.search,
      groups: this.filter.groups,
      assessments: this.filter.assessments,
    } as Filter;
    this.applyFilter();
  }

  applyFilter() {
    const search = `${this.filter.search}`;
    this.page$.pipe(first()).subscribe((page) => {
      const queryParams = page
        .filter(search)
        .filterStatuses(this.filter.status)
        .filterAssessments(this.filter.assessments)
        .filterGroups(this.filter.groups)
        .setOffset(0)
        .params();
      this._router.navigate(['/candidates'], {
        queryParams,
        queryParamsHandling: 'merge',
      });
    });
  }

  createCandidate() {
    const dialogRef = this._modalDialog.open(
      CandidateCreationModalComponent,
      new SynDialogConfig('modal-component')
    );
    dialogRef.afterClosed().subscribe(() => {
      this._router.navigateByUrl('');
    });
  }
}
